/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LicensingModule
 */

import {
    ALBServicesConnectivityStatus,
    ICLSStatus,
    IConfigPbAttributes,
    IOrgServiceUnits,
    ISaasLicensingStatus,
    LicenseTierType,
} from 'generated-types';

/**
 * Interface for license data structure from API.
 */
export interface ILicense {
    license_id: string;
    license_name?: string;
    license_type?: string;
    tier_type?: string;
    service_cores?: number;
    cores?: number;
    sockets?: number;
    valid_until?: string;
    start_on?: string;
}

/**
 * Interface for license list response from API.
 */
export interface ILicenseListResponse {
    licenses: ILicense[];
}

/**
 * Interface for licensing status response.
 */
export interface ILicensingStatus {
    cluster_name?: string;
    portal_url?: string;
    connectivity_status?: ALBServicesConnectivityStatus;
    last_refresh?: string;
    error?: string;
}

export interface ILicensingStatusResponse {
    configpb_attributes?: IConfigPbAttributes;
    saas_status?: ISaasLicensingStatus;
    uuid?: string;
    service_update?: {
        service_units?: IOrgServiceUnits;
    };
    cls_status?: ICLSStatus;
    legacy_license_grace_period?: string;
    support_legacy_license?: boolean;
}

export interface ILicenseTierUsageData {
    total: number;
    used: number;
}

export interface ILicenseTiersUsageData {
    [LicenseTierType.ENTERPRISE]: ILicenseTierUsageData;
    [LicenseTierType.BASIC]: ILicenseTierUsageData;
}

export interface ILicenseSummary {
    description?: string;
    key_?: string;
    label?: string;
    tiers?: ILicenseTiersUsageData;
}

/**
 * License status alert type enum.
 */
export enum AlertType {
    info = 'info',
    none = 'none',
    success = 'success',
    danger = 'danger',
    default = 'default',
}

export interface ILicenseAddAlert {
    type?: AlertType,
    message?: string,
}

/**
 * Interface for on-prem licensing details.
 */
export interface IOnPremLicensingDetails {
    hostname?: string;
    uuid?: string;
    name?: string;
    status?: string;
    avi_client_cert?: string;
    client_cert?: string;
    feature?: string;
    ingress_cert?: string;
    tenant_ref?: string;
    url?: string;
}
